import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import type { SatelliteImageList, SatelliteImageMeta } from './satelliteImagery';

// Mirrors DTOs/SatelliteDtos.cs.
interface FeatureIndexStat {
  date: string;
  clearFraction: number;
  sampleCount: number;
  ndviMean: number | null;
  ndviP10: number | null;
  ndviP50: number | null;
  ndviP90: number | null;
  ndreMean: number | null;
}

type SatelliteStatus = 'NotConfigured' | 'NoBoundary' | 'Off' | 'Pending' | 'Paused' | 'Ready' | 'Error';

interface SatelliteSummary {
  configured: boolean;
  farmId: string | null;
  farmName: string | null;
  monitoring: boolean;
  backfillFrom: string | null;
  status: SatelliteStatus;
  pausedUntil: string | null;
  resolutionMetres: number | null;
  syncedThrough: string | null;
  lastAttemptAt: string | null;
  lastError: string | null;
  bufferMetres: number | null;
  areaHectares: number | null;
  stats: FeatureIndexStat[];
}

// A pass counts when at least this much of the feature was cloud-free land.
const MIN_CLEAR_FRACTION = 0.8;
// Rolling-median window for the trend line.
const MEDIAN_WINDOW_DAYS = 45;
// Below this, 10 m pixels along the boundary are a large share of the
// feature, so values mix in whatever is next door.
const SMALL_AREA_HA = 2;
const DAY_MS = 86_400_000;

type Range = '1y' | '3y' | 'all';
const RANGE_DAYS: Record<Range, number | null> = { '1y': 365, '3y': 3 * 365, all: null };

interface ChartRow {
  t: number;
  ndvi: number | null;
  ndre: number | null;
  ndviTrend: number | null;
  ndreTrend: number | null;
}

const median = (values: number[]): number => {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

const toTime = (date: string) => Date.parse(`${date}T00:00:00Z`);

const formatDate = (t: number) =>
  new Date(t).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// Clear passes only, each with a centred rolling-median trend.
const buildRows = (stats: FeatureIndexStat[]): ChartRow[] => {
  const clear = stats
    .filter((s) => s.clearFraction >= MIN_CLEAR_FRACTION && s.ndviMean != null)
    .map((s) => ({ t: toTime(s.date), ndvi: s.ndviMean, ndre: s.ndreMean }));
  const half = (MEDIAN_WINDOW_DAYS / 2) * DAY_MS;
  return clear.map((row) => {
    const window = clear.filter((w) => Math.abs(w.t - row.t) <= half);
    const ndvis = window.map((w) => w.ndvi).filter((v): v is number => v != null);
    const ndres = window.map((w) => w.ndre).filter((v): v is number => v != null);
    return {
      ...row,
      ndviTrend: ndvis.length ? median(ndvis) : null,
      ndreTrend: ndres.length ? median(ndres) : null,
    };
  });
};

// Categorical slots 1 (blue) and 2 (orange), stepped per mode.
const useSeriesColours = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return {
    ndvi: dark ? '#3987e5' : '#2a78d6',
    ndre: dark ? '#d95926' : '#eb6834',
    grid: theme.palette.divider,
    axis: theme.palette.text.secondary,
  };
};

const tickYear = (t: number) => new Date(t).getUTCFullYear().toString();
const tickMonth = (t: number) =>
  new Date(t).toLocaleDateString('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });

// Explicit ticks on calendar boundaries (1 Jan / quarter / month starts) —
// recharts' own time ticks land mid-year, so year-only labels repeat.
const timeTicks = (min: number, max: number) => {
  const spanDays = (max - min) / DAY_MS;
  const stepMonths = spanDays > 540 ? 12 : spanDays > 200 ? 3 : 1;
  const start = new Date(min);
  const year = start.getUTCFullYear();
  let month = Math.ceil((start.getUTCMonth() + 1) / stepMonths) * stepMonths;
  const ticks: number[] = [];
  for (;;) {
    const t = Date.UTC(year + Math.floor(month / 12), month % 12, 1);
    if (t > max) break;
    ticks.push(t);
    month += stepMonths;
  }
  return { ticks, format: stepMonths === 12 ? tickYear : tickMonth };
};

interface ChartProps {
  rows: ChartRow[];
  showNdre: boolean;
  height: number;
  compact?: boolean;
}

const IndexChart: React.FC<ChartProps> = ({ rows, showNdre, height, compact }) => {
  const colours = useSeriesColours();
  if (rows.length === 0) return null;
  const { ticks, format } = timeTicks(rows[0].t, rows[rows.length - 1].t);
  // One legend entry per index — the per-pass dots share its colour.
  // recharts' plainline icon reads payload.strokeDasharray, so it must exist.
  const legendPayload = [
    { value: 'NDVI', type: 'plainline' as const, color: colours.ndvi, id: 'ndvi', payload: { strokeDasharray: '' } },
    ...(showNdre
      ? [{ value: 'NDRE', type: 'plainline' as const, color: colours.ndre, id: 'ndre', payload: { strokeDasharray: '' } }]
      : []),
  ];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 6, right: compact ? 8 : 16, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={colours.grid} />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          ticks={ticks}
          interval={0}
          tickFormatter={format}
          tick={{ fontSize: compact ? 10 : 12, fill: colours.axis }}
          stroke={colours.grid}
        />
        <YAxis
          domain={[0, 1]}
          ticks={compact ? [0, 0.5, 1] : [0, 0.2, 0.4, 0.6, 0.8, 1]}
          tick={{ fontSize: compact ? 10 : 12, fill: colours.axis }}
          stroke={colours.grid}
          width={compact ? 28 : 40}
        />
        <Tooltip
          labelFormatter={(t) => formatDate(Number(t))}
          formatter={(value: number, name: string) => [value == null ? '—' : value.toFixed(2), name]}
          cursor={{ stroke: colours.axis, strokeDasharray: '3 3' }}
        />
        {!compact && <Legend verticalAlign="top" height={28} payload={legendPayload} />}
        <Scatter name="NDVI (pass)" dataKey="ndvi" fill={colours.ndvi} fillOpacity={0.35} shape="circle" legendType="none" isAnimationActive={false} />
        <Line name="NDVI" dataKey="ndviTrend" stroke={colours.ndvi} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
        {showNdre && (
          <Scatter name="NDRE (pass)" dataKey="ndre" fill={colours.ndre} fillOpacity={0.35} shape="circle" legendType="none" isAnimationActive={false} />
        )}
        {showNdre && (
          <Line name="NDRE" dataKey="ndreTrend" stroke={colours.ndre} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const formatPu = (pu: number) => (pu < 0.1 ? 'under 0.1' : pu.toFixed(1));

interface SatellitePanelProps {
  featureId: string;
  featureName: string;
  // Adds a fetched NDVI image to the map (see satelliteImagery.tsx). The
  // map-image controls are hidden without it.
  onShowImage?: (image: SatelliteImageMeta) => Promise<void>;
}

/**
 * Sentinel-2 monitoring for the selected map feature — the switch that
 * turns it on for the feature's farm, sync status, the per-pass NDVI/NDRE
 * history, and fetching one pass's NDVI image as a map layer (see
 * Controllers/SatelliteController.cs).
 */
const SatellitePanel: React.FC<SatellitePanelProps> = ({ featureId, featureName, onShowImage }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const [summary, setSummary] = useState<SatelliteSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [range, setRange] = useState<Range>('3y');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageList, setImageList] = useState<SatelliteImageList | null>(null);
  const [imageDate, setImageDate] = useState('');
  // The date whose image is being fetched and added, if any.
  const [fetchingDate, setFetchingDate] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await fetch(`/api/spatial-features/${featureId}/satellite`, { headers: authHeader });
      if (!res.ok) throw new Error(`Failed to load satellite data (${res.status})`);
      setSummary(await res.json());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load satellite data');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [featureId, authHeader]);

  useEffect(() => {
    setSummary(null);
    load();
  }, [load]);

  // While history is being fetched, poll so the chart fills in as each
  // year arrives.
  useEffect(() => {
    if (summary?.status !== 'Pending') return undefined;
    const timer = window.setInterval(() => load(true), 5000);
    return () => window.clearInterval(timer);
  }, [summary?.status, load]);

  const toggleMonitoring = async (on: boolean) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/spatial-features/${featureId}/satellite-watch`, {
        method: on ? 'PUT' : 'DELETE',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: on ? JSON.stringify({}) : undefined,
      });
      if (!res.ok) throw new Error(`Failed to turn monitoring ${on ? 'on' : 'off'} (${res.status})`);
      setSummary(await res.json());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to change monitoring');
    } finally {
      setSaving(false);
    }
  };

  const loadImageList = useCallback(async () => {
    try {
      const res = await fetch(`/api/spatial-features/${featureId}/satellite-images`, { headers: authHeader });
      if (!res.ok) throw new Error(`Failed to load saved images (${res.status})`);
      setImageList(await res.json());
    } catch (e) {
      setImageError(e instanceof Error ? e.message : 'Failed to load saved images');
    }
  }, [featureId, authHeader]);

  // Fetches the image (spending processing units unless it's already
  // saved), then hands it to the map.
  const showImage = async (date: string) => {
    if (!onShowImage) return;
    setFetchingDate(date);
    setImageError(null);
    try {
      const res = await fetch(`/api/spatial-features/${featureId}/satellite-images`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? `Failed to fetch the image (${res.status})`);
      }
      await onShowImage(await res.json());
      setImageDialogOpen(false);
      setDialogOpen(false);
      loadImageList();
    } catch (e) {
      setImageError(e instanceof Error ? e.message : 'Failed to fetch the image');
    } finally {
      setFetchingDate(null);
    }
  };

  const allRows = useMemo(() => buildRows(summary?.stats ?? []), [summary]);
  const rangeRows = useMemo(() => {
    const days = RANGE_DAYS[range];
    if (days == null || allRows.length === 0) return allRows;
    const cutoff = allRows[allRows.length - 1].t - days * DAY_MS;
    return allRows.filter((r) => r.t >= cutoff);
  }, [allRows, range]);
  const compactRows = useMemo(() => {
    if (allRows.length === 0) return allRows;
    const cutoff = allRows[allRows.length - 1].t - 2 * 365 * DAY_MS;
    return allRows.filter((r) => r.t >= cutoff);
  }, [allRows]);
  const latest = allRows.length ? allRows[allRows.length - 1] : null;
  const clearStats = useMemo(
    () => (summary?.stats ?? []).filter((s) => s.clearFraction >= MIN_CLEAR_FRACTION && s.ndviMean != null).reverse(),
    [summary],
  );

  const smallArea = summary?.areaHectares != null && summary.areaHectares < SMALL_AREA_HA;

  const savedDates = useMemo(() => new Set((imageList?.images ?? []).map((i) => i.date)), [imageList]);

  const openImageDialog = () => {
    setImageDate(clearStats[0]?.date ?? '');
    setImageError(null);
    setImageDialogOpen(true);
    loadImageList();
  };

  const openHistory = () => {
    setImageError(null);
    setDialogOpen(true);
    if (onShowImage) loadImageList();
  };

  const costLine = (date: string): string | null => {
    if (!imageList) return null;
    if (savedDates.has(date)) return 'Already saved, so showing it is free.';
    if (imageList.estimatedProcessingUnits == null) return null;
    return `Uses about ${formatPu(imageList.estimatedProcessingUnits)} PU of this month's satellite budget `
      + `(${Math.round(imageList.monthProcessingUnits).toLocaleString()} of ${Math.round(imageList.monthlyBudget).toLocaleString()} used). `
      + `Pixels are about ${Math.round(imageList.resolutionMetres ?? 10)} m.`;
  };

  const statusLine = (): React.ReactNode => {
    if (!summary) return null;
    switch (summary.status) {
      case 'NotConfigured':
        return "Satellite data isn't set up on this server.";
      case 'NoBoundary':
        return 'Draw a boundary to get satellite history.';
      case 'Off':
        return `Monitoring is off for ${summary.farmName ?? 'this farm'}.`;
      case 'Pending':
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <CircularProgress size={12} />
            {summary.syncedThrough
              ? `Fetching history… through ${formatDate(toTime(summary.syncedThrough))}`
              : 'Fetching history…'}
          </Box>
        );
      case 'Paused':
        return `Paused: this month's satellite data budget is used up${
          summary.syncedThrough ? ` (history through ${formatDate(toTime(summary.syncedThrough))})` : ''
        }. Resumes ${summary.pausedUntil ? formatDate(toTime(summary.pausedUntil)) : 'next month'}.`;
      case 'Ready':
        return summary.syncedThrough ? `Checked through ${formatDate(toTime(summary.syncedThrough))}` : null;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2">Satellite (Sentinel-2)</Typography>
        {summary && summary.configured && summary.status !== 'NoBoundary' && (
          <MuiTooltip title={`Applies to the whole farm: ${summary.farmName ?? ''}`}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Monitor</Typography>
              <Switch
                size="small"
                checked={summary.monitoring}
                disabled={saving}
                onChange={(e) => toggleMonitoring(e.target.checked)}
                inputProps={{ 'aria-label': 'Satellite monitoring for this farm' }}
              />
            </Box>
          </MuiTooltip>
        )}
      </Box>

      {loading && !summary && <CircularProgress size={16} />}
      {error && <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>{error}</Alert>}

      {summary?.status === 'Error' && (
        <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
          Couldn't fetch satellite data — retrying hourly.
          {summary.lastError && (
            <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>{summary.lastError}</Typography>
          )}
        </Alert>
      )}

      {summary && (
        <Typography variant="caption" color="text.secondary" display="block">{statusLine()}</Typography>
      )}

      {summary && smallArea && summary.status !== 'NoBoundary' && (
        <Typography variant="caption" color="text.secondary" display="block">
          Under {SMALL_AREA_HA} ha: 10 m pixels along the boundary take in whatever is next door, so treat values as rough.
        </Typography>
      )}

      {latest && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: 0.5 }}>
            <Box>
              <Typography variant="h6" component="span">{latest.ndvi?.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                NDVI · {formatDate(latest.t)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              {onShowImage && (
                <Button size="small" startIcon={<AddPhotoAlternateOutlinedIcon fontSize="small" />} onClick={openImageDialog}>
                  Map
                </Button>
              )}
              <Button size="small" startIcon={<OpenInFullIcon fontSize="small" />} onClick={openHistory}>
                History
              </Button>
            </Box>
          </Box>
          <IndexChart rows={compactRows} showNdre={false} height={110} compact />
          <Typography variant="caption" color="text.secondary" display="block">
            Last 2 years · clear passes only · line = {MEDIAN_WINDOW_DAYS}-day median
          </Typography>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{featureName} — Sentinel-2 history</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {clearStats.length} clear passes (≥{Math.round(MIN_CLEAR_FRACTION * 100)}% cloud-free) of {summary?.stats.length ?? 0}.
              Dots are single passes; lines are the {MEDIAN_WINDOW_DAYS}-day median.
              {summary?.bufferMetres ? ` Boundary shrunk ${summary.bufferMetres} m to leave out edge pixels.` : ''}
              {summary?.resolutionMetres && summary.resolutionMetres > 10
                ? ` Large area: sampled on a ${summary.resolutionMetres} m grid (one real 10 m pixel in each ${summary.resolutionMetres / 10} × ${summary.resolutionMetres / 10} block) to keep costs down.`
                : ''}
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && setRange(v)}>
              <ToggleButton value="1y">1 yr</ToggleButton>
              <ToggleButton value="3y">3 yrs</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <IndexChart rows={rangeRows} showNdre height={340} />
          <TableContainer sx={{ maxHeight: 280, mt: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Clear</TableCell>
                  <TableCell align="right">NDVI mean</TableCell>
                  <TableCell align="right">NDVI p10–p90</TableCell>
                  <TableCell align="right">NDRE mean</TableCell>
                  <TableCell align="right">Pixels</TableCell>
                  {onShowImage && <TableCell padding="checkbox" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {clearStats.map((s) => (
                  <TableRow key={s.date}>
                    <TableCell>{formatDate(toTime(s.date))}</TableCell>
                    <TableCell align="right">{Math.round(s.clearFraction * 100)}%</TableCell>
                    <TableCell align="right">{s.ndviMean?.toFixed(2) ?? '—'}</TableCell>
                    <TableCell align="right">
                      {s.ndviP10 != null && s.ndviP90 != null ? `${s.ndviP10.toFixed(2)}–${s.ndviP90.toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell align="right">{s.ndreMean?.toFixed(2) ?? '—'}</TableCell>
                    <TableCell align="right">{s.sampleCount}</TableCell>
                    {onShowImage && (
                      <TableCell padding="checkbox">
                        {fetchingDate === s.date ? (
                          <CircularProgress size={18} sx={{ m: 0.5 }} />
                        ) : (
                          <MuiTooltip title={savedDates.has(s.date) ? 'Show NDVI image on the map (saved, free)' : 'Show NDVI image on the map'}>
                            <span>
                              <IconButton size="small" disabled={fetchingDate != null} onClick={() => showImage(s.date)}>
                                <AddPhotoAlternateOutlinedIcon fontSize="small" color={savedDates.has(s.date) ? 'primary' : 'inherit'} />
                              </IconButton>
                            </span>
                          </MuiTooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {imageError && <Alert severity="error" sx={{ mt: 1 }}>{imageError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageDialogOpen} onClose={() => fetchingDate == null && setImageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{featureName}: NDVI image on the map</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            size="small"
            label="Satellite pass"
            value={imageDate}
            onChange={(e) => setImageDate(e.target.value)}
            sx={{ mt: 1 }}
            helperText={`Clear passes only (≥${Math.round(MIN_CLEAR_FRACTION * 100)}% cloud-free), newest first`}
          >
            {clearStats.map((s) => (
              <MenuItem key={s.date} value={s.date}>
                {formatDate(toTime(s.date))} · NDVI {s.ndviMean?.toFixed(2)} · {Math.round(s.clearFraction * 100)}% clear
                {savedDates.has(s.date) ? ' · saved' : ''}
              </MenuItem>
            ))}
          </TextField>
          {imageDate && costLine(imageDate) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{costLine(imageDate)}</Typography>
          )}
          {imageError && <Alert severity="error" sx={{ mt: 1.5 }}>{imageError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)} disabled={fetchingDate != null}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => showImage(imageDate)}
            disabled={!imageDate || fetchingDate != null}
            startIcon={fetchingDate != null ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Add to map
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SatellitePanel;
